import type {MyUser} from '@deezer/next/contexts/main/types';
import {StakeholderType} from '@deezer/next/contexts/main/types';
import type {
	ArtistFormData,
	ClaimFormData,
	LabelFormData,
	ManagerFormData,
	ProviderFormData,
} from '@deezer/next/features/ClaimAccess/types';
import {
	ClaimAccessType,
	ClaimScreen,
} from '@deezer/next/features/ClaimAccess/types';
import {
	defaultArtistFormData,
	defaultLabelFormData,
	defaultManagerFormData,
	defaultProviderFormData,
} from '@deezer/next/features/ClaimAccess/constants';
import type {
	QueryFormData,
	WithExtra,
} from '@deezer/next/features/ClaimAccess/queryTypes';

export const assertNever = (x: never): never => {
	throw new Error(`Unhandled case: ${x}`);
};

export const getDefaultClaimDataState = (
	claimTypeNumber?: StakeholderType,
): ClaimFormData => {
	switch (claimTypeNumber) {
		case StakeholderType.ARTIST:
			return defaultArtistFormData;
		case StakeholderType.ARTIST_MANAGER:
			return defaultManagerFormData;
		case StakeholderType.LABEL_GROUP:
			return defaultLabelFormData;
		case StakeholderType.PROVIDER:
			return defaultProviderFormData;
		default:
			return defaultManagerFormData;
	}
};

export const formatDataHelper = <
	T extends ClaimAccessType,
	E extends object = {},
>(
	formData: ClaimFormData,
	claimType: T,
	extra?: E,
): WithExtra<QueryFormData<T>, E> | null => {
	switch (claimType) {
		case ClaimAccessType.ARTIST: {
			const {
				artist,
				artistUpc,
				instagram,
				facebook,
				twitter,
				website,
				displayName,
			} = formData as ArtistFormData;
			if (!artist) return null;
			const base = {
				artistId: artist.id,
				artistUpc,
				displayName,
				instagram,
				facebook,
				twitter,
				website,
			};
			return {...base, ...(extra ?? {})} as WithExtra<QueryFormData<T>, E>;
		}
		case ClaimAccessType.MANAGER: {
			const list = (formData as ManagerFormData).managerArtists.map(
				({id, upc, instagram, facebook, twitter, website}) => ({
					artistId: id,
					artistUpc: upc,
					instagram,
					facebook,
					twitter,
					website,
					...(extra ?? {}),
				}),
			);
			return list as WithExtra<QueryFormData<T>, E>;
		}
		case ClaimAccessType.LABEL: {
			const list = (formData as LabelFormData).labelSubLabels.map(
				({id, upc, name, providerId, providerName}) => ({
					labelName: name,
					labelId: id,
					labelUpc: upc,
					providerId,
					providerName,
					...(extra ?? {}),
				}),
			);
			return list as WithExtra<QueryFormData<T>, E>;
		}
		case ClaimAccessType.PROVIDER: {
			const {provider, providerUpc1, providerUpc2} =
				formData as ProviderFormData;
			if (!provider) return null;
			const base = {
				providerId: provider.id,
				providerUpc1,
				providerUpc2,
			};
			return {...base, ...(extra ?? {})} as WithExtra<QueryFormData<T>, E>;
		}
		default:
			return assertNever(claimType);
	}
};

export const getDefaultClaimScreen = (user?: MyUser) => {
	const userStakeholderType = user?.stakeholderType;

	switch (userStakeholderType) {
		case StakeholderType.PROVIDER:
			return ClaimScreen.NEW_PROVIDER;
		case StakeholderType.LABEL_GROUP:
			return ClaimScreen.LABEL_LIST;
		case StakeholderType.ARTIST_MANAGER:
			return ClaimScreen.MANAGER_ARTIST_LIST;
		case StakeholderType.ARTIST:
			return ClaimScreen.NEW_ARTIST;
		case StakeholderType.ARTIST_MARKETING:
			return ClaimScreen.SELECT_CLAIM_TYPE;
		default:
			return ClaimScreen.SELECT_CLAIM_TYPE;
	}
};
